// ─── Accepting an access link ────────────────────────────────────────────────
//
// The page a link opens. It exchanges the token for a real session as that
// person and drops them on Home — no password to choose and no form to fill
// in, because for this model the link itself is the credential.
//
// It sits OUTSIDE the sign-in gate, necessarily: the whole point is that
// somebody with no session can open it.
//
// ── It LOOKS on load and CLAIMS on a tap, and that is not a nicety ────────
//
// Farndale's version of this page used to claim the account on mount: no tap,
// no confirmation, just a fetch as soon as it rendered. An access link is
// single-use, so ANYTHING that opened the URL destroyed it — a link preview
// generated by a messaging app, a safety scanner following links, a mis-tap,
// or somebody checking the link worked before sending it. Josh Parker's link
// was burned exactly that way, by a check meant only to confirm it loaded
// (2026-08-23).
//
// So the lookup is a read and the only thing that spends the token is a
// button pressed on the device that will hold the session. An automated fetch
// can look and cannot claim — which also means Drea can safely check a link
// works before she sends it, which was everybody's instinct in the first
// place.
//
// ── It must never fail silently ───────────────────────────────────────────
//
// Somebody tapping a link on a phone gets either "you're in" or a plain
// sentence saying why not. Never a blank screen that reads as a broken app.

import { useEffect, useState } from "react";
import { Message as Notice, Loading } from "@nulife/app-ui";
import * as api from "./api.js";

type State =
  | { status: "looking" }
  /** Identified, NOT yet claimed — the token is still unspent here. */
  | { status: "ready"; name: string; organisationName: string | null }
  | { status: "claiming"; name: string; organisationName: string | null }
  | { status: "in"; name: string }
  | { status: "failed"; why: string };

export function AcceptInvite({ token, onIn }: {
  token: string;
  /** Called once there is a real session, so the app can re-ask who is
   *  signed in and render itself. */
  onIn: () => void;
}) {
  const [state, setState] = useState<State>({ status: "looking" });

  // ── Look. A read, no side effect, safe to repeat ─────────────────────────
  useEffect(() => {
    if (!token) {
      setState({ status: "failed", why: "That link is missing its code." });
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const seen = await api.previewInvite(token);
        if (!cancelled) setState({ status: "ready", ...seen });
      } catch (err) {
        if (cancelled) return;
        setState({
          status: "failed",
          why: err instanceof Error ? err.message : "That link did not work.",
        });
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  // ── Claim. Only from here, only on a press ───────────────────────────────
  const claim = async () => {
    if (state.status !== "ready") return;
    setState({ ...state, status: "claiming" });
    try {
      const done = await api.acceptInvite(token);
      setState({ status: "in", name: done.name });
      // A beat on "You're in", so it is not a flash of text nobody reads.
      setTimeout(onIn, 700);
    } catch (err) {
      setState({
        status: "failed",
        why: err instanceof Error ? err.message : "That link could not be used.",
      });
    }
  };

  return (
    <div
      data-testid="accept-invite"
      /**
       * ── `data-nl-design`, and why this page needs it explicitly ─────────
       *
       * The pale-card tokens (`--nl-band`, `--nl-band-ink`, and their
       * stroke) are defined ONLY under `[data-nl-design]`, and this app gets
       * that attribute from chassis's `AppFrame`. This page renders outside
       * the frame on purpose — somebody with no session has to be able to
       * open it — so without this the accept button resolved to no
       * background, no border and inherited ink: a bold line of text that
       * did not look like a button at all.
       *
       * Caught in the probe screenshot, not by any assertion: every
       * measurement was green while the one control on the page was
       * invisible as a control. The same class of fault as the edit box
       * inside the light card earlier today, and the reason the screenshots
       * get looked at rather than just filed.
       */
      data-nl-design=""
      style={{
        minHeight: "70vh", display: "flex", flexDirection: "column",
        alignItems: "center", justifyContent: "center", gap: 16,
        padding: 24, textAlign: "center",
      }}
    >
      {state.status === "looking" && <Loading />}

      {(state.status === "ready" || state.status === "claiming") && (
        <>
          <p style={{ margin: 0, color: "var(--nl-ink-dim)" }}>
            {state.organisationName ?? "This committee"} has given you access.
          </p>
          <h1 data-testid="invite-name"
              style={{ margin: 0, fontSize: "1.6rem", color: "var(--nl-ink)" }}>
            {state.name}
          </h1>
          {/* Said before the tap, because the tap is the irreversible part:
              the link works once. */}
          <p style={{ margin: 0, fontSize: "0.85rem", color: "var(--nl-ink-faint)", maxWidth: 320 }}>
            This link works once, on this device. Tap below and it will sign
            you in and stay signed in.
          </p>
          <button
            type="button"
            data-testid="invite-accept"
            disabled={state.status === "claiming"}
            onClick={() => void claim()}
            style={{
              minHeight: "var(--nl-hit-min)", padding: "0 22px",
              borderRadius: "var(--nl-radius-sm)", font: "inherit", fontWeight: 600,
              background: "var(--nl-band)", color: "var(--nl-band-ink)",
              border: "1px solid var(--nl-band-stroke)",
              cursor: state.status === "claiming" ? "default" : "pointer",
              opacity: state.status === "claiming" ? 0.6 : 1,
            }}
          >
            {state.status === "claiming" ? "Signing you in…" : `Yes, I'm ${state.name}`}
          </button>
        </>
      )}

      {state.status === "in" && (
        <p data-testid="invite-done" style={{ margin: 0, fontSize: "1.2rem", color: "var(--nl-ink)" }}>
          You're in, {state.name}.
        </p>
      )}

      {state.status === "failed" && (
        <div data-testid="invite-failed" style={{ maxWidth: 360 }}>
          <Notice tone="bad">{state.why}</Notice>
          {/* No retry button. A used link does not start working because
              somebody pressed it again, and offering one would suggest it
              might. */}
          <p style={{ marginTop: 12, fontSize: "0.85rem", color: "var(--nl-ink-faint)" }}>
            Ask whoever sent it to send a new one — links can only be used once.
          </p>
        </div>
      )}
    </div>
  );
}
